import React, { useEffect, useRef, useState } from "react";
import {
  useAnnouncement,
  fetchAnnouncement,
  saveAnnouncementApi,
  uploadAnnouncementImage,
  getStoredApiKey,
  setStoredApiKey,
} from "../../hooks/useAnnouncement";
import { useNotification } from "../../hooks/useNotification";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

function AdminAnnouncements() {
  const notify = useNotification();
  const current = useAnnouncement();

  const [apiKey, setApiKey] = useState(getStoredApiKey());
  const [apiKeySaved, setApiKeySaved] = useState(!!getStoredApiKey());

  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Hydrate form when server data arrives
  useEffect(() => {
    if (current) {
      setText(current.text || "");
      setImage(current.image || "");
    }
  }, [current?.updatedAt]);

  const saveKey = () => {
    setStoredApiKey(apiKey.trim());
    setApiKeySaved(!!apiKey.trim());
    notify.success(apiKey.trim() ? "API key saved" : "API key cleared");
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!apiKey) return notify.error("Enter and save your admin API key first");
    setUploading(true);
    try {
      const { url } = await uploadAnnouncementImage(file, apiKey);
      setImage(url);
      notify.success("Image uploaded");
    } catch (err) {
      notify.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    if (!apiKey) return notify.error("Enter and save your admin API key first");
    setSaving(true);
    try {
      await saveAnnouncementApi({ text: text.trim(), image: image.trim() }, apiKey);
      notify.success("Announcement published");
      // Refresh from server so we display the canonical value
      const fresh = await fetchAnnouncement();
      if (fresh) {
        setText(fresh.text || "");
        setImage(fresh.image || "");
      }
    } catch (err) {
      notify.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!apiKey) return notify.error("Enter and save your admin API key first");
    if (!window.confirm("Clear the announcement for all users?")) return;
    setSaving(true);
    try {
      await saveAnnouncementApi({ text: "", image: "" }, apiKey);
      setText("");
      setImage("");
      notify.info("Announcement cleared");
    } catch (err) {
      notify.error(err.message || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* API Key setup */}
      <div className="section-header">
        <div className="section-title">Admin API Key</div>
      </div>
      <Card className="mb-lg">
        <p className="text-muted mb-md" style={{ fontSize: 12 }}>
          Paste the key you set in <code style={{ color: "var(--cyan)" }}>public_html/api/config.php</code>.
          Saved to this browser only.
        </p>
        <div className="input-group" style={{ marginBottom: 8 }}>
          <input
            type="password"
            className="input"
            placeholder="Paste your admin_api_key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
          <Button variant="primary" onClick={saveKey}>
            {apiKeySaved ? "Update Key" : "Save Key"}
          </Button>
          <Button variant="ghost" onClick={() => { setApiKey(""); setStoredApiKey(""); setApiKeySaved(false); notify.info("Key cleared"); }}>
            Forget
          </Button>
        </div>
        {apiKeySaved && (
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--success)", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--success)" }} />
            Key stored on this device
          </div>
        )}
      </Card>

      {/* Editor */}
      <div className="section-header">
        <div className="section-title">News / Announcement</div>
      </div>
      <Card className="mb-lg">
        <div className="input-group">
          <label className="input-label">Message</label>
          <textarea
            className="input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Big update rolling out this weekend..."
            style={{ minHeight: 80, resize: "vertical", fontFamily: "var(--font-body)" }}
            maxLength={500}
          />
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, textAlign: "right" }}>
            {text.length} / 500
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Image</label>

          {/* File picker */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 12px",
            background: "#050815",
            border: "1px dashed var(--cyan-border)",
            borderRadius: 8,
          }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={doUpload}
              style={{ flex: 1, color: "var(--text-secondary)", fontSize: 12 }}
              disabled={uploading || !apiKey}
            />
            {uploading && <span className="spinner" />}
          </div>

          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            JPG / PNG / WEBP / GIF · Max 2 MB
          </div>

          {/* Or paste URL */}
          <div style={{ marginTop: 10 }}>
            <input
              type="url"
              className="input"
              value={image}
              onChange={(e) => setImage(e.target.value)}
              placeholder="…or paste an external image URL"
            />
          </div>

          {image && (
            <div style={{
              marginTop: 10, padding: 8,
              background: "#050815",
              border: "1px solid var(--border-strong)",
              borderRadius: 8,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <img
                src={image}
                alt=""
                style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover", border: "1px solid var(--cyan-border)" }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
              <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: "var(--cyan)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {image}
              </div>
              <button
                onClick={() => setImage("")}
                style={{ background: "transparent", border: "1px solid var(--border-strong)", color: "var(--text-muted)", padding: "3px 8px", borderRadius: 4, fontSize: 10, cursor: "pointer" }}
              >
                REMOVE
              </button>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
          <Button variant="primary" onClick={publish} loading={saving} disabled={!apiKey}>
            Save & Publish
          </Button>
          <Button variant="ghost" onClick={clearAll} loading={saving} disabled={!apiKey}>
            Clear
          </Button>
        </div>
      </Card>

      {/* Preview */}
      {(text || image) && (
        <>
          <div className="section-header">
            <div className="section-title">Live Preview</div>
          </div>
          <Card>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              background: "linear-gradient(90deg, rgba(0, 229, 255, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%)",
              border: "1px solid var(--cyan-border)",
              borderRadius: 10,
              overflow: "hidden",
            }}>
              {image && (
                <img
                  src={image}
                  alt=""
                  style={{ height: 22, width: 22, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--cyan-border)", flexShrink: 0 }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              <div style={{ fontSize: 12, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {text || "(no message)"}
              </div>
            </div>
          </Card>
        </>
      )}

      {current && current.updatedAt > 0 && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12, textAlign: "center" }}>
          Last published: {new Date(current.updatedAt * 1000).toLocaleString("en-US", { hour12: false, timeZone: "UTC" })} UTC
        </div>
      )}
    </div>
  );
}

export default AdminAnnouncements;
